import { localeStorageLanguageKey } from 'config/config';
import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, IconButton, Stack, Button } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import useMobile from 'services/Hooks/useMobile';

const images = [
  { imgSrc: '/images/headers/carousel-img-en-01.jpg', imgPhSrc: '/images/headers/carousel-img-en-01_lqip.jpg' },
  { imgSrc: '/images/headers/carousel-img-en-02.jpg', imgPhSrc: '/images/headers/carousel-img-en-02_lqip.jpg' },
  { imgSrc: '/images/headers/carousel-img-en-03.jpg', imgPhSrc: '/images/headers/carousel-img-en-03_lqip.jpg' },
];

const SLIDE_MS = 6000;
const XFADE_MS = 800;
const AUTOPLAY_RESUME_MS = 15000;

const defaultSlides = images.map((image, index) => ({
  id: `default-${index}`,
  imgSrc: image.imgSrc,
  useBackgroundColor: false,
  backgroundColor: null,
  title: `Header ${index + 1}`,
  linkUrl: '',
}));

const normalizeTextColor = value => {
  const normalizedValue = (value || '').trim();
  return /^#([0-9A-F]{3}){1,2}$/i.test(normalizedValue) ? normalizedValue : '#FFFFFF';
};

const ImageCarousel = ({ fullHeight = false, slides = [] }) => {
  const isMobile = useMobile();
  const effectiveSlides = slides.length > 0 ? slides : defaultSlides;
  const hasMultipleSlides = effectiveSlides.length > 1;

  const [current, setCurrent] = useState(0);
  const [isFading, setIsFading] = useState(false);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const timerRef = useRef(null);
  const autoplayResumeRef = useRef(null);

  let language = localStorage.getItem(localeStorageLanguageKey) || 'fr';
  const fallbackButtonLabel = language === 'fr' ? 'Ouvrir' : 'Open';
  const buttonLabel = (effectiveSlides[current]?.buttonText || '').trim() || fallbackButtonLabel;
  const carouselHeight = fullHeight ? '100vh' : isMobile ? 220 : 220;
  const headerMarginTop = fullHeight ? 0 : isMobile ? (language === 'fr' ? 48 : 32) : 0;
  const baseImageStyle = {
    width: '100%',
    height: carouselHeight,
    objectFit: 'cover',
  };

  // Preload once; each URL requested exactly once
  useEffect(() => {
    effectiveSlides
      .filter(src => !src?.useBackgroundColor && src?.imgSrc)
      .forEach(src => {
        const img = new Image();
        img.decoding = 'async';
        img.src = src.imgSrc;
      });
  }, [effectiveSlides]);

  // Auto-advance without changing any img.src
  useEffect(() => {
    if (!hasMultipleSlides || !isAutoPlaying) {
      clearInterval(timerRef.current);
      setIsFading(false);
      return () => {};
    }

    const prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const start = () => {
      clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        if (prefersReducedMotion) {
          setCurrent(p => (p === effectiveSlides.length - 1 ? 0 : p + 1));
          return;
        }
        setIsFading(true);
        setTimeout(() => {
          setCurrent(p => (p === effectiveSlides.length - 1 ? 0 : p + 1));
          setIsFading(false);
        }, Math.min(400, XFADE_MS / 2));
      }, SLIDE_MS);
    };

    const onVis = () => (document.hidden ? clearInterval(timerRef.current) : start());
    document.addEventListener('visibilitychange', onVis);
    start();
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      clearInterval(timerRef.current);
    };
  }, [effectiveSlides.length, hasMultipleSlides, isAutoPlaying]);

  useEffect(
    () => () => {
      clearTimeout(autoplayResumeRef.current);
      clearInterval(timerRef.current);
    },
    [],
  );

  const pauseAndScheduleResume = () => {
    setIsAutoPlaying(false);
    clearTimeout(autoplayResumeRef.current);
    autoplayResumeRef.current = setTimeout(() => {
      setIsAutoPlaying(true);
    }, AUTOPLAY_RESUME_MS);
  };

  const jumpToSlide = delta => {
    if (!hasMultipleSlides) return;
    pauseAndScheduleResume();
    setCurrent(prev => (prev + delta + effectiveSlides.length) % effectiveSlides.length);
  };

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        overflow: 'hidden',
        marginTop: headerMarginTop,
      }}
      aria-roledescription="carousel"
      aria-label="Homepage header carousel">
      <Box
        aria-hidden="true"
        sx={{
          width: '100%',
          height: carouselHeight,
          pointerEvents: 'none',
        }}
      />

      {/* Slides: mounted once, opacity-toggled → no re-fetches */}
      {effectiveSlides.map((src, i) => {
        const active = i === current && (!isFading || !hasMultipleSlides);

        if (src?.useBackgroundColor) {
          return (
            <Box
              key={src.id || `bg-${i}`}
              sx={{
                position: 'absolute',
                inset: 0,
                backgroundColor: src.backgroundColor || '#000000',
                opacity: active ? 1 : 0,
                transition: `opacity ${XFADE_MS}ms ease-in-out`,
                willChange: 'opacity',
              }}
              aria-hidden={!active}
            />
          );
        }

        return (
          <img
            key={src.id || src.imgSrc}
            src={src.imgSrc}
            alt={src.title || `Header ${i + 1}`}
            style={{
              ...baseImageStyle,
              position: 'absolute',
              top: 0,
              left: 0,
              display: 'block',
              opacity: active ? 1 : 0,
              transition: `opacity ${XFADE_MS}ms ease-in-out`,
              willChange: 'opacity',
              cursor: 'default',
            }}
            decoding="async"
            aria-hidden={!active}
            loading={i === 0 ? 'eager' : 'lazy'}
          />
        );
      })}

      {effectiveSlides[current]?.linkUrl ? (
        effectiveSlides[current]?.isExternalRoute ? (
          <Box
            component="a"
            href={effectiveSlides[current].linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 2,
              display: 'block',
              textDecoration: 'none',
            }}
            aria-label={effectiveSlides[current]?.title || 'carousel link'}
          />
        ) : (
          <Box
            component={RouterLink}
            to={effectiveSlides[current].linkUrl}
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 2,
              display: 'block',
              textDecoration: 'none',
            }}
            aria-label={effectiveSlides[current]?.title || 'carousel link'}
          />
        )
      ) : null}

      {effectiveSlides[current]?.title || effectiveSlides[current]?.subtitle ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            background: 'linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.72) 100%)',
            pointerEvents: 'none',
          }}
        />
      ) : null}

      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 3,
          color: '#fff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          px: { xs: 3, md: 6 },
          pointerEvents: 'none',
        }}>
        {effectiveSlides[current]?.title ? (
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: 32, sm: 40, md: 52, lg: 64 },
              lineHeight: 1.1,
              fontWeight: 900,
              color: normalizeTextColor(effectiveSlides[current]?.titleColor),
              textShadow: '0 2px 12px rgba(0,0,0,0.65)',
            }}>
            {effectiveSlides[current].title}
          </Typography>
        ) : null}
        {effectiveSlides[current]?.subtitle ? (
          <Typography
            variant="body1"
            sx={{
              mt: 1.5,
              maxWidth: 720,
              mx: 'auto',
              fontSize: { xs: 15, md: 18 },
              lineHeight: 1.6,
              fontWeight: 400,
              color: normalizeTextColor(effectiveSlides[current]?.subtitleColor),
              textShadow: '0 2px 12px rgba(0,0,0,0.65)',
            }}>
            {effectiveSlides[current].subtitle}
          </Typography>
        ) : null}
      </Box>

      {effectiveSlides[current]?.showButton && effectiveSlides[current]?.linkUrl ? (
        effectiveSlides[current]?.isExternalRoute ? (
          <Button
            component="a"
            href={effectiveSlides[current].linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            color="secondary"
            size="large"
            disableElevation
            sx={{
              mt: 4,
              py: { md: 2, lg: 3 },
              borderRadius: 0,
              boxShadow: 'none',
              position: 'absolute',
              right: { xs: 16, md: 24 },
              bottom: { xs: 16, md: 24 },
              zIndex: 5,
              '&:hover': {
                backgroundColor: '#EFBA29',
                boxShadow: 'none',
              },
              '&:active': {
                boxShadow: 'none',
              },
              '&.Mui-focusVisible': {
                boxShadow: 'none',
              },
            }}>
            <Typography variant="h1" sx={{ color: '#000', fontSize: { xs: 16, md: 24 }, letterSpacing: '-0.02em' }}>
              {buttonLabel}
            </Typography>
          </Button>
        ) : (
          <Button
            component={RouterLink}
            to={effectiveSlides[current].linkUrl}
            variant="contained"
            color="secondary"
            size="large"
            disableElevation
            sx={{
              mt: 4,
              py: { md: 2, lg: 3 },
              borderRadius: 0,
              boxShadow: 'none',
              position: 'absolute',
              right: { xs: 16, md: 24 },
              bottom: { xs: 16, md: 24 },
              zIndex: 5,
              '&:hover': {
                backgroundColor: '#EFBA29',
                boxShadow: 'none',
              },
              '&:active': {
                boxShadow: 'none',
              },
              '&.Mui-focusVisible': {
                boxShadow: 'none',
              },
            }}>
            <Typography variant="h1" sx={{ color: '#000', fontSize: { xs: 16, md: 20, lg: 24 }, letterSpacing: '-0.02em' }}>
              {buttonLabel}
            </Typography>
          </Button>
        )
      ) : null}

      {hasMultipleSlides ? (
        <>
          <IconButton
            onClick={() => jumpToSlide(-1)}
            sx={theme => ({
              position: 'absolute',
              left: { xs: 8, md: 16 },
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 4,
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              '&:hover': { bgcolor: theme.palette.primary.light },
            })}>
            <ChevronLeft />
          </IconButton>
          <IconButton
            onClick={() => jumpToSlide(1)}
            sx={theme => ({
              position: 'absolute',
              right: { xs: 8, md: 16 },
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 4,
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              '&:hover': { bgcolor: theme.palette.primary.light },
            })}>
            <ChevronRight />
          </IconButton>

          <Stack
            direction="row"
            spacing={1}
            sx={{
              position: 'absolute',
              bottom: 18,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 4,
            }}>
            {effectiveSlides.map((slide, index) => (
              <Box
                key={slide.id || slide.imgSrc || index}
                role="button"
                tabIndex={0}
                onClick={() => {
                  pauseAndScheduleResume();
                  setCurrent(index);
                }}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    pauseAndScheduleResume();
                    setCurrent(index);
                  }
                }}
                sx={theme => ({
                  width: current === index ? 28 : 12,
                  height: 12,
                  borderRadius: 6,
                  bgcolor: current === index ? theme.palette.primary.main : 'rgba(255,255,255,0.55)',
                  cursor: 'pointer',
                  transition: 'all 0.25s ease',
                  '&:hover': {
                    bgcolor: current === index ? theme.palette.primary.main : 'rgba(255,255,255,0.8)',
                  },
                })}
              />
            ))}
          </Stack>
        </>
      ) : null}
    </div>
  );
};

export default ImageCarousel;
